import { createTheme } from "@mui/material/styles";

// CSS variables are safe in styleOverrides — they pass through to CSS as-is.
// They are NOT safe in palette — MUI runs JS color math (alpha, decomposeColor)
// on palette values which crashes on var(--...) strings.
const v = (variable: string) => `var(${variable})`;

const theme = createTheme({
  // ── Palette — must use real hex values ────────────────────────────────────
  // These are the Highland theme defaults (the app's existing look).
  // Theme switching for MUI internals (ripple, hover overlays) is handled
  // via styleOverrides below using CSS variables.
  palette: {
    primary: {
      main:         "#0D2D4A",   // --brand-primary (highland)
      light:        "#112f50",   // --brand-primary-hover
      dark:         "#091f34",   // --brand-primary-active
      contrastText: "#FFFFFF",
    },
    secondary: {
      main:         "#EBF2FF",
      contrastText: "#0D2D4A",
    },
    error: {
      main:         "#ff1744",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#EAEDF2",   // --bg-page (highland)
      paper:   "#FFFFFF",   // --bg-surface
    },
    text: {
      primary:   "#0A1929",   // --text-primary (highland)
      secondary: "#3D4F66",   // --text-secondary
      disabled:  "#7E8FA8",   // --text-muted
    },
    divider: "#EDF0F5",       // --border-section
  },

  // ── Typography — preserved from original ──────────────────────────────────
  typography: {
    fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: "2.2rem", fontWeight: 500 },
    h2: { fontSize: "1.8rem", fontWeight: 500 },
    body1: { fontSize: "1rem" },
    button: { textTransform: "none", fontWeight: 600 },
  },

  // ── Spacing ───────────────────────────────────────────────────────────────
  spacing: 8,

  // ── Component overrides — CSS variables ARE safe here ─────────────────────
  // styleOverrides produce CSS strings — no JS color parsing happens.
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: v("--bg-surface"),
          color:           v("--text-primary"),
          borderColor:     v("--border-default"),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: v("--bg-surface"),
          color:           v("--text-primary"),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: v("--bg-surface-raised"),
          borderTop:       "1px solid var(--border-section)",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: v("--bg-surface"),
          color:           v("--text-primary"),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color:           v("--text-primary"),
          backgroundColor: v("--bg-input"),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--border-default"),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--border-hover"),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--border-focus"),
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: v("--text-label") },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: v("--text-primary"),
          "&:hover":        { backgroundColor: v("--bg-chip-hover") },
          "&.Mui-selected": {
            backgroundColor: v("--bg-chip"),
            "&:hover":       { backgroundColor: v("--bg-chip-hover") },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color:       v("--text-primary"),
          borderColor: v("--border-section"),
        },
        head: {
          color:           v("--text-muted"),
          backgroundColor: v("--bg-surface-raised"),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: v("--bg-chip"),
          color:           v("--text-primary"),
          borderColor:     v("--border-default"),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: v("--text-primary"),
          color:           v("--bg-surface"),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // Only override background/border — do NOT override color here.
        // MUI derives the ripple/hover overlay from palette.primary.main (hex),
        // so those keep working. We just redirect the surface colors.
        outlined: {
          borderColor: v("--border-default"),
          color:       v("--text-secondary"),
          "&:hover": {
            backgroundColor: v("--bg-chip"),
            borderColor:     v("--border-hover"),
          },
        },
        contained: {
          backgroundColor: v("--brand-primary"),
          "&:hover": {
            backgroundColor: v("--brand-primary-hover"),
          },
          "&:disabled": {
            backgroundColor: v("--border-default"),
            color:           v("--text-muted"),
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: v("--border-section") },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: v("--bg-surface"),
          borderColor:     v("--border-default"),
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: v("--text-primary"),
          "&:hover":        { backgroundColor: v("--bg-chip") },
          "&.Mui-selected": {
            backgroundColor: v("--bg-chip"),
            color:           v("--brand-primary"),
            "&:hover":       { backgroundColor: v("--bg-chip-hover") },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: v("--bg-header"),
        },
      },
    },
  },
});

export default theme;